import React, { useState } from "react";
import { api, API, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UploadSimple, Trash, MagicWand, Warning, Plus, TrendUp, CheckCircle, ArrowsClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";

/** Searchable vendor picker. Pulls saved vendors from /suggestions/vendors-detail
 *  and, on select, auto-fills GSTIN / email / address from purchase history.
 *  Still allows free typing for a brand-new vendor. */
export function VendorPicker({ value, onChange, onSelectVendor, testid, placeholder }) {
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = React.useRef(null);

  const search = (val) => {
    onChange(val);
    if (timer.current) clearTimeout(timer.current);
    if (!val.trim()) { setResults([]); setOpenList(false); return; }
    setOpenList(true);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suggestions/vendors-detail", { params: { q: val, limit: 10 } });
        setResults(data?.vendors || []);
        setOpenList(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
  };

  return (
    <div className="relative">
      <Input
        data-testid={testid || "vendor-search"}
        value={value}
        onChange={(e) => search(e.target.value)}
        onFocus={() => { if (value && value.trim()) setOpenList(true); }}
        onBlur={() => setTimeout(() => setOpenList(false), 180)}
        placeholder={placeholder || "Start typing — pick from history to auto-fill GST"}
        autoComplete="off"
      />
      {openList && (loading || results.length > 0 || (value || "").trim()) && (
        <div data-testid="vendor-results" className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {loading && <div className="px-3 py-2 text-[11px] text-slate-400">Searching…</div>}
          {!loading && results.length === 0 && (value || "").trim() && (
            <div data-testid="vendor-empty" className="px-3 py-2 text-[11px] text-slate-500 leading-snug">
              New vendor — GST details will be remembered after you save this PO.
            </div>
          )}
          {results.map((v, idx) => (
            <button
              key={`vend-${idx}`}
              type="button"
              data-testid={`vendor-opt-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); onSelectVendor(v); setResults([]); setOpenList(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-orange-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-[12.5px] text-slate-800 truncate">{v.name}</div>
              <div className="text-[10px] text-slate-500 flex gap-2 flex-wrap">
                <span className={v.gstin ? "font-mono text-slate-600" : "text-amber-600"}>{v.gstin ? `GSTIN ${v.gstin}` : "no GSTIN on file"}</span>
                {v.email && <span>· {v.email}</span>}
                {v.po_count > 0 && <span>· {v.po_count} PO{v.po_count === 1 ? "" : "s"}</span>}
                {v.buy_count > 0 && <span>· {v.buy_count} buy{v.buy_count === 1 ? "" : "s"}</span>}
                {v.gst_rate ? <span>· GST {v.gst_rate}%</span> : null}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/** Searchable "add from purchase history" picker. Pulls materials from
 *  /suggestions/materials (description + HSN + GST + unit + last price). */
export function MaterialPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = React.useRef(null);

  const search = (val) => {
    setQ(val);
    if (timer.current) clearTimeout(timer.current);
    if (!val.trim()) { setResults([]); setOpenList(false); return; }
    setOpenList(true);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suggestions/materials", { params: { q: val, limit: 10 } });
        setResults(Array.isArray(data) ? data : (data?.materials || data?.items || []));
        setOpenList(true);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1 border border-slate-300 rounded-sm px-2 h-7 bg-white focus-within:border-[#EA580C]">
        <MagnifyingGlass size={12} className="text-slate-400" />
        <input
          data-testid="vpo-material-search"
          value={q}
          onChange={(e) => search(e.target.value)}
          onFocus={() => q && setOpenList(true)}
          onBlur={() => setTimeout(() => setOpenList(false), 180)}
          placeholder="Add item from history…"
          className="text-[11.5px] outline-none w-48 bg-transparent"
        />
      </div>
      {openList && (results.length > 0 || loading || q.trim()) && (
        <div data-testid="vpo-material-results" className="absolute right-0 z-50 mt-1 w-80 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {loading && <div className="px-3 py-2 text-[11px] text-slate-400">Searching…</div>}
          {!loading && results.length === 0 && q.trim() && (
            <div data-testid="vpo-material-empty" className="px-3 py-2 text-[11px] text-slate-500 leading-snug">
              No matching purchase history. <span className="text-slate-400">Run Purchases → Import History to load past buys, or just type the item manually below.</span>
            </div>
          )}
          {results.map((m, idx) => (
            <button
              key={`mat-${idx}`}
              data-testid={`vpo-material-opt-${idx}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(m); setQ(""); setResults([]); setOpenList(false); }}
              className="w-full text-left px-3 py-1.5 hover:bg-orange-50 border-b border-slate-100 last:border-0"
            >
              <div className="text-[12px] text-slate-800 whitespace-normal break-words leading-snug" title={m.description}>{m.description}</div>
              <div className="text-[10px] text-slate-500 flex gap-2">
                {m.hsn && <span>HSN {m.hsn}</span>}
                {m.unit && <span>· {m.unit}</span>}
                {m.last_unit_price > 0 && <span>· last {fmtMoney(m.last_unit_price)}</span>}
                {m.usage_count > 0 && <span>· {m.usage_count}× bought</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

/** Per-line price indicator vs purchase history. Recomputes live from the
 *  current rate so editing a rate updates the flag instantly. Shows the
 *  cheapest supplier so the buyer knows who to call to match the price. */
export function PriceFlag({ r, loading, rate, onMatch }) {
  if (!r || r.status === "no_history" || r.lowest_rate == null) {
    return <span className="text-[11px] text-slate-400" title={loading ? "Checking purchase history…" : "No prior purchase of this material"}>{loading ? "…" : "new"}</span>;
  }
  const lo = Number(r.lowest_rate) || 0;
  const above = (Number(rate) || 0) > lo + 0.01;
  const pct = lo > 0 ? Math.round(((Number(rate) || 0) - lo) / lo * 1000) / 10 : 0;
  let d = "";
  try { d = r.lowest_date ? new Date(r.lowest_date).toLocaleDateString("en-IN", { month: "short", year: "2-digit" }) : ""; } catch { d = r.lowest_date || ""; }
  const vendor = r.lowest_vendor || "";
  // Cheapest buy of the same item from a DIFFERENT supplier (negotiation lever)
  const otherRate = r.other_rate != null ? Number(r.other_rate) : null;
  const otherVendor = r.other_vendor || "";
  const otherCheaper = otherRate != null && otherVendor && otherRate < (Number(rate) || 0) - 0.01;
  const title = `Lowest ${fmtMoney(lo)}${r.lowest_unit ? "/" + r.lowest_unit : ""}` +
    `${vendor ? " — " + vendor : ""}${d ? ", " + d : ""}` +
    ` · avg ${fmtMoney(r.avg_rate)} · last ${fmtMoney(r.last_rate)} · ${r.history_count} past buy${r.history_count === 1 ? "" : "s"}` +
    (otherVendor ? ` · other vendor: ${fmtMoney(otherRate)} at ${otherVendor}` : "");
  const subline = otherCheaper
    ? <span data-testid="price-flag-other-vendor" className="inline-block text-[10px] text-red-600 leading-tight max-w-[120px] truncate font-medium">↘ {fmtMoney(otherRate)} at {otherVendor}</span>
    : (vendor ? <span data-testid="price-flag-vendor" className="inline-block text-[10px] text-slate-500 leading-tight max-w-[120px] truncate">low {fmtMoney(lo)} · {vendor}</span> : null);
  if (above) {
    return (
      <div className="flex flex-col items-end gap-0.5 min-w-0 overflow-hidden">
        <span data-testid="price-flag-high" title={title} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5 cursor-help">
          <TrendUp size={11} weight="bold" /> +{pct}%
        </span>
        {subline}
        {onMatch && (
          <button data-testid="price-flag-match" onClick={() => onMatch(lo)} title={`Set rate to your cheapest past price ${fmtMoney(lo)}`}
            className="text-[10px] font-semibold text-[#EA580C] hover:underline">
            Match {fmtMoney(lo)}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-0.5 min-w-0 overflow-hidden cursor-help" title={title}>
      <span data-testid="price-flag-best" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5">
        <CheckCircle size={11} weight="fill" /> best
      </span>
      {subline}
    </div>
  );
}

/**
 * VendorPoUploadSheet — mirrors the Invoice / Expense upload flow.
 * User picks a Tally PO PDF → server extracts vendor + items → user reviews /
 * tweaks the parsed form → save via POST /api/vendor-pos.
 */
export function VendorPoUploadSheet({ projects, onUploaded, open: openProp, onOpenChange, hideTrigger }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChange || setOpenInternal;

  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);   // once parsed → editable object
  const [projectId, setProjectId] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [bench, setBench] = useState({});          // desc(lowercase) -> benchmark row
  const [benchLoading, setBenchLoading] = useState(false);
  const [vendorGst, setVendorGst] = useState(18);  // vendor's typical GST% for new lines

  const reset = () => {
    setFile(null); setParsing(false); setSaving(false); setForm(null);
    setProjectId(""); setAutoSend(false); setBench({}); setBenchLoading(false); setVendorGst(18);
  };

  const checkPrices = async (its, vendorName) => {
    const list = (its || []).filter((i) => (i.description || "").trim());
    if (!list.length) { setBench({}); return; }
    setBenchLoading(true);
    try {
      const { data } = await api.post("/purchase-analytics/price-benchmark", {
        exclude_vendor: vendorName || "",
        items: list.map((i) => ({ description: i.description, hsn: i.hsn || "", unit_price: Number(i.unit_price) || 0 })),
      });
      const map = {};
      (data.rows || []).forEach((r) => { map[(r.description || "").trim().toLowerCase()] = r; });
      setBench(map);
    } catch (e) {
      /* benchmark is advisory — never block PO creation on it */
    } finally {
      setBenchLoading(false);
    }
  };

  const parse = async (f) => {
    if (!f) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/vendor-pos/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({
        vendor: data.vendor || "",
        vendor_gstin: data.vendor_gstin || "",
        vendor_email: data.vendor_email || "",
        vendor_address: data.vendor_address || "",
        po_number: "",
        issue_date: data.issue_date || new Date().toISOString().slice(0, 10),
        expected_date: data.expected_date || "",
        terms: data.terms || "",
        ship_to: "",
        ship_to_address: "",
        other_reference: data.po_number || "",
        payment_terms: data.payment_terms || "",
        delivery_terms: data.delivery_terms || "",
        items: data.items || [],
      });
      toast.success(`Extracted ${data.line_count} line${data.line_count === 1 ? "" : "s"} from ${f.name}`);
      checkPrices(data.items || [], data.vendor || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const updateItem = (idx, key, value) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    }));
  };
  const patchItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const removeItem = (idx) => {
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };
  const addItem = () => {
    setForm((f) => ({
      ...f,
      items: [...(f.items || []), { description: "", hsn: "", qty: 1, unit: "Nos", unit_price: 0, gst_rate: vendorGst }],
    }));
  };
  const addItemFromHistory = (m) => {
    const newItem = {
      description: m.description || "",
      hsn: m.hsn || "",
      qty: 1,
      unit: m.unit || "Nos",
      unit_price: Number(m.last_unit_price) || 0,
      gst_rate: Number(m.gst_rate) || vendorGst,
    };
    setForm((f) => {
      const items = [...(f.items || []), newItem];
      checkPrices(items, f.vendor);
      return { ...f, items };
    });
    toast.success(`Added "${(m.description || "").slice(0, 40)}" from history`);
  };

  const subtotal = (form?.items || []).reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const tax = (form?.items || []).reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * Number(it.gst_rate || 0) / 100, 0);
  const grand = subtotal + tax;

  const save = async () => {
    if (!form?.vendor || !form.items?.length) {
      toast.error("Vendor + at least one line item required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        project_id: projectId || null,
        auto_send_email: autoSend,
        // Ensure numbers; clamp qty>=0.001 (never 0 or negative)
        items: form.items.map((it) => {
          const qty = Math.max(Number(it.qty) || 0, 0);
          return {
            description: it.description,
            hsn: it.hsn || "",
            qty: qty > 0 ? qty : 1,
            unit: it.unit || "Nos",
            unit_price: Math.max(Number(it.unit_price) || 0, 0),
            gst_rate: Math.max(Number(it.gst_rate) || 0, 0) || 18,
          };
        }),
      };
      const { data: created } = await api.post("/vendor-pos", payload);
      toast.success(`Purchase Order created — ${created?.po_number || form.po_number || "(auto-numbered)"}`);
      if (created?.id) {
        // Auto-open the PO on Celsius letterhead so it can be printed/shared
        window.open(`${API}/vendor-pos/${created.id}/pdf`, "_blank", "noopener");
      }
      reset();
      setOpen(false);
      onUploaded?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      {!hideTrigger && (
        <SheetTrigger asChild>
          <Button data-testid="upload-vpo-button" variant="outline" className="rounded-sm border-slate-300">
            <UploadSimple size={16} className="mr-2" /> Upload Proforma (PI)
          </Button>
        </SheetTrigger>
      )}
      <SheetContent className="w-[720px] sm:max-w-2xl lg:w-[min(920px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <MagicWand size={18} className="text-[#EA580C]" /> Upload Supplier Proforma → Generate PO
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {!form ? (
            <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center hover:border-[#EA580C] transition-colors">
              <input
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); parse(f); } }}
                className="hidden"
                id="vpo-file-input"
                data-testid="vpo-file-input"
              />
              <label htmlFor="vpo-file-input" className="cursor-pointer">
                <UploadSimple size={28} className="mx-auto text-slate-400 mb-2" />
                <div className="text-sm font-semibold text-slate-800">Click to upload the supplier's Proforma Invoice (PI)</div>
                <div className="text-xs text-slate-500 mt-1">
                  {parsing ? "Reading proforma… please wait" : "I'll extract vendor, items, HSN & rates, then generate a Purchase Order on your Celsius letterhead."}
                </div>
                {file && !parsing && <div className="text-xs text-slate-600 mt-2 font-mono">{file.name}</div>}
              </label>
            </div>
          ) : (
            <>
              {/* --- Vendor + PO metadata --- */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vendor Name">
                  <VendorPicker
                    value={form.vendor}
                    onChange={(name) => setForm((f) => ({ ...f, vendor: name }))}
                    onSelectVendor={(v) => {
                      const g = Number(v.gst_rate) || 18;
                      setVendorGst(g);
                      setForm((f) => ({
                        ...f,
                        vendor: v.name,
                        vendor_gstin: v.gstin || f.vendor_gstin,
                        vendor_email: v.email || f.vendor_email,
                        vendor_address: v.address || f.vendor_address,
                        payment_terms: v.payment_terms || f.payment_terms,
                        delivery_terms: v.delivery_terms || f.delivery_terms,
                        terms: v.terms || f.terms,
                        items: (f.items || []).map((it) => (!(it.description || "").trim() ? { ...it, gst_rate: g } : it)),
                      }));
                    }}
                    testid="vpo-vendor-input"
                  />
                </Field>
                <Field label="Vendor GSTIN">
                  <Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value })} className="font-mono uppercase" />
                </Field>
                <Field label="Vendor Email">
                  <Input value={form.vendor_email} onChange={(e) => setForm({ ...form, vendor_email: e.target.value })} />
                </Field>
                <Field label="PO Number">
                  <Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="Auto-numbered if blank" data-testid="vpo-number-input" />
                </Field>
                <Field label="PO Date">
                  <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
                </Field>
                <Field label="Expected Delivery">
                  <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
                </Field>
                <div className="col-span-2">
                  <Field label="Vendor Address">
                    <Textarea rows={2} value={form.vendor_address} onChange={(e) => setForm({ ...form, vendor_address: e.target.value })} />
                  </Field>
                </div>
                <Field label="Link Project (optional)">
                  <Select value={projectId || "__none__"} onValueChange={(v) => {
                    const pid = v === "__none__" ? "" : v;
                    setProjectId(pid);
                    const proj = projects?.find((p) => p.id === pid);
                    const siteAddr = proj ? ([proj.site, proj.location].filter(Boolean).join(", ") || proj.client_address || "") : "";
                    if (siteAddr) setForm((f) => ({ ...f, ship_to_address: siteAddr, ship_to: f.ship_to || proj.name || "Site" }));
                  }}>
                    <SelectTrigger data-testid="vpo-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {projects?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Payment Terms">
                  <Input value={form.payment_terms || ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 50% advance" data-testid="vpo-payment-terms-input" />
                </Field>
                <Field label="Delivery Terms">
                  <Input value={form.delivery_terms || ""} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} placeholder="e.g. Within 2 weeks, at site" data-testid="vpo-delivery-terms-input" />
                </Field>
                <Field label="Terms">
                  <Input value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="e.g. Net 30" />
                </Field>
                <Field label="Other Reference">
                  <Input value={form.other_reference || ""} onChange={(e) => setForm({ ...form, other_reference: e.target.value })} placeholder="e.g. Quotation / enquiry no." data-testid="vpo-other-ref-input" />
                </Field>
                <div className="col-span-2">
                  <Field label="Consignee / Ship-to (optional — defaults to buyer)">
                    <Textarea rows={2} value={form.ship_to_address || ""} onChange={(e) => setForm({ ...form, ship_to_address: e.target.value, ship_to: form.ship_to || (e.target.value ? (form.ship_to || "Site") : "") })} placeholder="Delivery site / project address" data-testid="vpo-shipto-input" />
                  </Field>
                </div>
              </div>

              {/* --- Items table --- */}
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Line Items · {form.items.length}</div>
                  <div className="flex items-center gap-3">
                    <MaterialPicker onPick={addItemFromHistory} />
                    <button onClick={() => checkPrices(form.items, form.vendor)} data-testid="vpo-recheck-prices" disabled={benchLoading}
                      className="text-[11px] font-semibold text-slate-600 hover:text-[#EA580C] flex items-center gap-1 disabled:opacity-50">
                      <ArrowsClockwise size={12} className={benchLoading ? "animate-spin" : ""} /> {benchLoading ? "Checking…" : "Re-check prices"}
                    </button>
                    <button onClick={addItem} data-testid="vpo-add-item-btn" className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1">
                      <Plus size={12} /> Add row
                    </button>
                  </div>
                </div>
                <Table className="bc-list text-[12.5px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-24">HSN</TableHead>
                      <TableHead className="w-20 text-right">Qty</TableHead>
                      <TableHead className="w-16">Unit</TableHead>
                      <TableHead className="w-28 text-right">Rate</TableHead>
                      <TableHead className="w-20 text-right">GST%</TableHead>
                      <TableHead className="w-28 text-right">Line ₹</TableHead>
                      <TableHead className="w-40 text-right">vs History</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, i) => {
                      const line = Number(it.qty || 0) * Number(it.unit_price || 0);
                      return (
                        <TableRow key={`vpo-line-${i}`} data-testid={`vpo-line-${i}`}>
                          <TableCell>
                            <MaterialAutocomplete
                              value={it.description}
                              onChange={(v) => updateItem(i, "description", v)}
                              onPick={(m) => patchItem(i, {
                                description: m.description || "",
                                hsn: m.hsn || it.hsn || "",
                                unit: m.unit || it.unit || "Nos",
                                unit_price: Number(m.last_unit_price) || Number(it.unit_price) || 0,
                                gst_rate: Number(m.gst_rate) || Number(it.gst_rate) || vendorGst,
                              })}
                              placeholder="Search material…"
                              testid={`vpo-line-desc-${i}`}
                            />
                          </TableCell>
                          <TableCell><Input value={it.hsn} onChange={(e) => updateItem(i, "hsn", e.target.value)} className="h-7 font-mono text-[11.5px]" /></TableCell>
                          <TableCell><Input type="number" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" /></TableCell>
                          <TableCell><Input value={it.unit || "Nos"} onChange={(e) => updateItem(i, "unit", e.target.value)} className="h-7 text-[11.5px]" data-testid={`vpo-unit-${i}`} /></TableCell>
                          <TableCell><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" /></TableCell>
                          <TableCell><Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, "gst_rate", e.target.value)} className="h-7 text-right font-mono-num text-[12.5px]" /></TableCell>
                          <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(line)}</TableCell>
                          <TableCell className="text-right"><PriceFlag r={bench[(it.description || "").trim().toLowerCase()]} loading={benchLoading} rate={Number(it.unit_price) || 0} onMatch={(val) => updateItem(i, "unit_price", val)} /></TableCell>
                          <TableCell><button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button></TableCell>
                        </TableRow>
                      );
                    })}
                    {form.items.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-500 text-[12px]">No items yet — add from history or click Add row</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* --- Totals --- */}
              <div className="flex justify-end">
                <div className="w-64 text-[13px] space-y-1 font-mono-num">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>GST</span><span>{fmtMoney(tax)}</span></div>
                  <div className="flex justify-between font-semibold text-[15px] text-[#EA580C] pt-1 border-t border-slate-200"><span>Grand Total</span><span>{fmtMoney(grand)}</span></div>
                </div>
              </div>

              {/* --- Price-vs-history flags --- */}
              {(() => {
                const flagged = (form.items || []).filter((it) => {
                  const r = bench[(it.description || "").trim().toLowerCase()];
                  return r && r.lowest_rate != null && (Number(it.unit_price) || 0) > r.lowest_rate + 0.01;
                });
                const hasHistory = Object.values(bench).some((r) => r && r.lowest_rate != null);
                if (benchLoading && !hasHistory) return null;
                if (flagged.length > 0) {
                  return (
                    <div data-testid="price-flag-banner" className="text-[12px] bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-sm flex items-start gap-1.5">
                      <Warning size={15} weight="fill" className="mt-0.5 shrink-0" />
                      <span><b>{flagged.length}</b> of {form.items.length} line{form.items.length === 1 ? "" : "s"} priced <b>above your lowest historical rate</b> — negotiate before raising this PO. Hover a red flag to see the cheapest past rate, supplier and date.</span>
                    </div>
                  );
                }
                if (hasHistory) {
                  return (
                    <div data-testid="price-flag-banner-ok" className="text-[12px] bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-sm flex items-center gap-1.5">
                      <CheckCircle size={15} weight="fill" className="shrink-0" /> Every priced line is at or below your best historical rate.
                    </div>
                  );
                }
                return null;
              })()}

              {/* --- Warnings + Save --- */}
              {!form.vendor && (
                <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm flex items-center gap-1.5">
                  <Warning size={14} weight="fill" /> Vendor name missing — please fill.
                </div>
              )}

              <div className="flex items-center gap-3">
                <input id="vpo-auto-send" type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
                <label htmlFor="vpo-auto-send" className="text-[12px] text-slate-600">Also email the PO PDF to <b>{form.vendor_email || "vendor"}</b></label>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} data-testid="vpo-reupload-btn" className="rounded-sm">Re-upload</Button>
                <Button
                  onClick={save}
                  disabled={saving || !form.vendor || form.items.length === 0}
                  data-testid="vpo-save-btn"
                  className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
                >
                  {saving ? "Saving…" : "Save Vendor PO"}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
